import SegmentedControl, { ISegment } from '@travelnest/merlin.segmented-control';
import { Spacer } from '@travelnest/merlin.spacer';
import { Paragraph } from '@travelnest/merlin.typography';
import React from 'react';
import { IProperty } from '../../../data/entities/property';
import { IPropertyScore, IPropertyScoreSummary } from '../../../data/entities/property-score';
import { PropertyScoreSummary, typographyV5 } from '../../components/property-score/PropertyScoreSummary';
import { PropertySwitcher } from '../../components/property-switcher/PropertySwitcher';
import {
	appendPotentialRecommendationScoreIncrease,
	appendScoreToProperty,
	categoriseRecommendations,
	getContextualHeading,
	IPropertyWithScore,
	propertyNeedsToPublish
} from './property-score-helpers';
import { RecommendationList } from './PropertyRecommendations';
import { SegmentTitle } from './SegmentTitle';

interface IPropertyScoreForPropertyProps {
	selectableProperties: IProperty[];
	selectablePropertyScores: IPropertyScoreSummary[];
	selectedProperty: IProperty;
	selectedPropertyScore: IPropertyScore;
	selectedPropertyScoreDiff: number;
	loading: boolean;
	customerId: string;
	activeSegment: number;
	currentlyPublishing: boolean;
	onChangeProperty: (id: string) => void;
	onPublishProperty: (id: number) => void;
	snoozeRecommendation: (customerId: string, propertyId: number, attributeName: string) => void;
	markAttributesAsViewed: (customerId: string, propertyId: number) => void;
	setActiveSegment: (segmentIndex: number) => void;
}

export const PropertyScoreForProperty = ({
	selectableProperties,
	selectablePropertyScores,
	selectedProperty,
	selectedPropertyScore,
	selectedPropertyScoreDiff,
	loading,
	customerId,
	activeSegment,
	currentlyPublishing,
	onChangeProperty,
	onPublishProperty,
	snoozeRecommendation,
	markAttributesAsViewed,
	setActiveSegment,
}: IPropertyScoreForPropertyProps) => {
	const propertiesWithScore = appendScoreToProperty(selectableProperties, selectablePropertyScores);
	const stableProps = { customerId, snoozeRecommendation };
	const { todo, done, snoozed } = categoriseRecommendations(appendPotentialRecommendationScoreIncrease(selectedPropertyScore.attributes));
	const { property_id, score } = selectedPropertyScore;
	const segments = [
		{
			id: 'tab1',
			ariaLabel: todo.text,
			title: <SegmentTitle text={todo.text} unread={todo.unread} count={todo.count} />,
			view: (
				<React.Fragment key="tab1">
					<Spacer />
					<Paragraph style={typographyV5.p}>Recommendations with the biggest impact on your score are shown first.</Paragraph>
					<Spacer />
					<RecommendationList
						{...stableProps}
						recommendations={todo.recommendations}
					/>
				</React.Fragment>
			)
		},
		{
			id: 'tab2',
			ariaLabel: done.text,
			title: <SegmentTitle text={done.text} unread={done.unread} count={done.count} />,
			onSegmentClick: () => markAttributesAsViewed(customerId, selectedProperty.id),
			view: (
				<React.Fragment key="tab2">
					<Spacer />
					<Paragraph style={typographyV5.p}>Great job! Here's where your property is already performing well.</Paragraph>
					<Spacer />
					<RecommendationList
						{...stableProps}
						recommendations={done.recommendations}
					/>
				</React.Fragment>
			),
		},
		{
			id: 'tab3',
			ariaLabel: snoozed.text,
			title: <SegmentTitle text={snoozed.text} unread={snoozed.unread} count={snoozed.count} />,
			view: (
				<React.Fragment key="tab3">
					<Spacer />
					<Paragraph style={typographyV5.p}>These recommendations stay here until you action them.</Paragraph>
					<Spacer />
					<RecommendationList
						{...stableProps}
						recommendations={snoozed.recommendations}
					/>
				</React.Fragment>
			)
		}
	] as ISegment[];

	return (
		<>
			<Spacer />
			<div data-testid="property-score-and-recos">
				<PropertyScoreSummary
					title={getContextualHeading(selectableProperties)}
					score={score}
					propertyId={property_id}
					publishChanges={onPublishProperty}
					hasUnpublishedChanges={propertyNeedsToPublish(selectedProperty)}
					scoreDiff={selectedPropertyScoreDiff}
					numberOfAttributes={selectedPropertyScore.attributes.length}
					currentlyPublishing={currentlyPublishing}
				>
					{
						selectableProperties.length > 1 &&
						<>
							<Spacer />
							<PropertySwitcher
								customerId={customerId}
								currentPropertyId={selectedProperty.legacy_property_id ?? selectedProperty.id.toString()}
								loading={loading}
								properties={propertiesWithScore}
								switchProperty={onChangeProperty}
								sortPropertiesBy={(a: IPropertyWithScore, b: IPropertyWithScore) => a.totalScore! - b.totalScore!}
							/>
						</>
					}
				</PropertyScoreSummary>
				<SegmentedControl
					id="property-score"
					segments={segments}
					activeSegment={activeSegment}
					handleViewChange={setActiveSegment}
				/>
			</div>
		</>
	);
};
